import { X } from '@/components/ui/icons';
import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'full';
  className?: string;
  /** Hide the default header (for custom layouts like profile cards). */
  bare?: boolean;
}

const sizes = {
  sm: 'max-w-[420px]',
  md: 'max-w-[520px]',
  lg: 'max-w-[760px]',
  full: 'max-w-none w-screen h-dvh max-h-none rounded-none',
};

/**
 * Accessible modal built on the native <dialog> element: the browser provides
 * focus trapping, Escape-to-close, inert background and top-layer stacking.
 */
export function Modal({ open, onClose, title, description, children, footer, size = 'md', className, bare }: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      // showModal() focuses the first focusable (usually the close button); let content opt in.
      dialog.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const onCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    dialog.addEventListener('cancel', onCancel);
    return () => dialog.removeEventListener('cancel', onCancel);
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      data-cf="modal"
      aria-labelledby={title ? 'modal-title' : undefined}
      onMouseDown={(e) => {
        // Close when the backdrop (the dialog element itself) is clicked.
        if (e.target === ref.current) onClose();
      }}
      className={cn(
        'm-auto max-h-[min(90dvh,860px)] w-[calc(100vw-24px)] overflow-hidden rounded-3xl border border-line bg-elevated p-0 text-fg shadow-pop',
        sizes[size],
        className,
      )}
    >
      {open && (
        <div className="flex max-h-[inherit] flex-col">
          {!bare && (title || description) && (
            <header className="relative px-6 pt-6 pb-2">
              {title && (
                <h2 id="modal-title" className="pr-8 text-[22px]">
                  {title}
                </h2>
              )}
              {description && <p className="mt-1.5 text-sm text-fg-muted">{description}</p>}
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="absolute top-5 right-5 rounded-full p-1.5 text-fg-muted transition-colors hover:bg-hover hover:text-fg"
              >
                <X size={20} />
              </button>
            </header>
          )}
          <div className={cn('min-h-0 flex-1 overflow-y-auto', !bare && 'px-6 py-4')}>{children}</div>
          {footer && <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-sidebar/60 px-6 py-4">{footer}</footer>}
        </div>
      )}
    </dialog>
  );
}
